import { Plus, Settings } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import type { MenuCategory } from "@/types/menu-category";

interface CategoryChipsProps {
  categories: Pick<MenuCategory, "id" | "name">[];
  /** The category the list is filtered to; undefined is "All". */
  activeId?: number;
  onChange: (id: number | undefined) => void;
  /** Only passed to people who may build menus, so the button is absent for the rest. */
  onAddCategory?: () => void;
  onConfigureCategories?: () => void;
}

const chipClass = (isActive: boolean) =>
  `h-auto rounded-full px-3.5 py-1.5 text-xs font-medium ${
    isActive
      ? "bg-(--color-action) text-(--color-action-fg) hover:bg-(--color-action)/90"
      : "bg-(--color-surface-chip-inactive) text-(--color-text-secondary) hover:bg-(--color-surface-accent)"
  }`;

/**
 * The category bar above the Menu Items list: "All" plus the selected concept's
 * categories in their saved order. Selecting a chip filters the list, "All"
 * clears it.
 */
export function CategoryChips({
  categories,
  activeId,
  onChange,
  onAddCategory,
  onConfigureCategories,
}: CategoryChipsProps) {
  const { t } = useLingui();

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="ghost"
        onClick={() => onChange(undefined)}
        aria-pressed={activeId === undefined}
        className={chipClass(activeId === undefined)}
      >
        {t`All`}
      </Button>
      {categories.map((category) => {
        const isActive = category.id === activeId;
        return (
          <Button
            key={category.id}
            type="button"
            variant="ghost"
            onClick={() => onChange(category.id)}
            aria-pressed={isActive}
            className={chipClass(isActive)}
          >
            {category.name}
          </Button>
        );
      })}

      {onAddCategory && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="gap-1 text-xs"
          onClick={onAddCategory}
        >
          <Plus className="size-3" />
          {t`Add Category`}
        </Button>
      )}
      {onConfigureCategories && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="ml-auto gap-1 text-xs"
          onClick={onConfigureCategories}
        >
          <Settings className="size-3" />
          {t`Configure categories`}
        </Button>
      )}
    </div>
  );
}
